// Durable screen drafts for the floor (spec 06 §4.4/§4.5 floor rows).
//
// The outbox guarantees that a SAVED tap survives anything. This hook covers
// the twenty minutes BEFORE the tap: the ticks, quantities and variance reasons
// an operator accumulates while unloading a truck, and the count values typed
// bin by bin. Held in `useState` alone they die with the process — and a PWA on
// a floor phone is killed by the OS routinely, not exceptionally.
//
// Written through to localStorage on every change (small payloads, and a debounce
// would be one more way to lose the last keystroke), rehydrated on mount, and
// dropped explicitly once the work has been enqueued — a draft that outlived its
// submission would re-tick a manifest that is already on its way.

import { useCallback, useRef, useState } from "react";
import { dropDraft, loadDraft, saveDraft } from "../lib/floor-read-cache";

export function useFloorDraft<T>(
  key: string,
  initial: T,
): {
  value: T;
  set: (next: T | ((prev: T) => T)) => void;
  clear: () => void;
} {
  // `useState`'s lazy initialiser, so the disk read happens once per mount and
  // not on every render of a twenty-line manifest.
  const [value, setValue] = useState<T>(() => {
    const stored = loadDraft(key);
    return stored === undefined ? initial : (stored as T);
  });
  const keyRef = useRef(key);
  keyRef.current = key;
  const initialRef = useRef(initial);

  const set = useCallback((next: T | ((prev: T) => T)) => {
    setValue((prev) => {
      const resolved = typeof next === "function" ? (next as (p: T) => T)(prev) : next;
      saveDraft(keyRef.current, resolved);
      return resolved;
    });
  }, []);

  const clear = useCallback(() => {
    dropDraft(keyRef.current);
    setValue(initialRef.current);
  }, []);

  return { value, set, clear };
}
